import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Tools|Bulk Authorization';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.BulkCandidateTools,
	portal: 'SP',
};

const htmlContent = `
<p>
Follow the instructions below to format a CSV file that will allow you to authorize exams in bulk.  \
Select the CSV file and select a Data Provider, then press the Authorize button to receive a CSV file with exam and authorization information for each exam code.  \
Active Date: date a candidate can register at the Data Provider.  Send OK Date: date when exam is allowed to be sent to Data Provider.  \
Please note these dates can be the same.
</p>
<p><strong>To create the Comma Separated File (CSV) import file: </strong></p>
<p>
Include the following column headers recognized by the parser. Extra fields will be ignored.  Omitting the required columns will result in an error.
</p>
<ul>
	<li>AWS ID - Required</li>
	<li>Exam Code - Required</li>
	<li>Send OK Date - Required</li>
	<li>Active Date - Required</li>
	<li>Expiration Date - if provided, must be after the Active date </li>
</ul>
<p><strong>Return CSV file: </strong></p>
<p>After selecting a Data Provider and pressing the Authorize button, a CSV file will be returned with following columns:
</p>
<ul>
	<li>AWS ID</li>
	<li>Exam Code</li>
	<li>Send OK Date</li>
	<li>Active Date</li>
	<li>Success or Error Message</li>
</ul>
`;

export type SPBulkExamAuthorizationLocNames =
	| 'SPBulkExamAuthorizationPageTitle'
	| 'SPBulkExamAuthorizationPageHeading'
	| 'SPBulkExamAuthorizationButton'
	| 'SPBulkExamAuthorizationInstructions'
	| 'SPBulkExamAuthorizationDataProviderLabel'
	| 'SPBulkExamAuthorizationDataProviderRequired'
	| 'SPBulkExamAuthorizationInvalidRowsError'
	| 'SPBulkAuthCurrentProgress'
	| 'SPBulkAuthProcessingError';

export const spBulkExamAuthorizationLocs: LocDefs<SPBulkExamAuthorizationLocNames> = {
	SPBulkExamAuthorizationPageTitle: {
		defaultValue: 'Authorization',
		description: 'Page title for the exam authorization page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPBulkExamAuthorizationPageHeading: {
		defaultValue: 'Candidate Tools',
		description: 'Page Heading for the exam authorization page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPBulkExamAuthorizationInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the exam authorization page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		...sharedProperties,
		htmlAllowed: true,
	},
	SPBulkExamAuthorizationButton: {
		defaultValue: 'Authorize',
		description: 'Button to authorize exams',
		displayPath: `${dpRoot}|Authorize Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPBulkExamAuthorizationDataProviderLabel: {
		defaultValue: 'Delivery Provider',
		description: 'Delivery Provider label for the authorization page',
		displayPath: `${dpRoot}|Delivery Provider`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPBulkExamAuthorizationDataProviderRequired: {
		defaultValue: 'Delivery Provider is required.',
		description: 'Error shown when delivery provider is not selected',
		displayPath: `${dpRoot}|Delivery Provider Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBulkExamAuthorizationInvalidRowsError: {
		defaultValue: 'No valid rows found.',
		description: 'Error shown when no valid rows are found in the CSV file',
		displayPath: `${dpRoot}|Invalid Rows Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPBulkAuthCurrentProgress: {
		defaultValue: 'Current Progress',
		description: 'Current progress text',
		displayPath: `${dpRoot}|Current Progress`,
		displayType: 'Content',
		...sharedProperties,
	},
	SPBulkAuthProcessingError: {
		defaultValue: 'An error occurred during bulk processing',
		description: 'Error shown when an error occurs during bulk processing',
		displayPath: `${dpRoot}|Processing Error`,
		displayType: 'Error',
		...sharedProperties,
	},
};

export const BULK_AUTHORIZATION_LOC_KEYS = MapLocKeys<SPBulkExamAuthorizationLocNames>(
	spBulkExamAuthorizationLocs
);
export type SPBulkExamAuthorizationLocType = LocMap<SPBulkExamAuthorizationLocNames>;
